import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

import PreferencesSetup from "@/components/onboarding/PreferencesSetup";
import PricingOffer from "@/components/onboarding/PricingOffer";
import SelfChatTest from "@/components/onboarding/SelfChatTest";
import RouteLoadingShell from "@/components/shared/RouteLoadingShell";
import SignedInSettingsShell from "@/components/shared/SignedInSettingsShell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  completeAccountOnboarding,
  createOnboardingHandoff,
  getBillingSubscription,
  getCurrentAccountId,
  getMyAccount,
  getPreferencesBootstrap,
  getOnboardingPromoOffer,
  listMyAccounts,
  readCachedBillingSubscription,
  readCachedMyAccount,
  readCachedMyAccounts,
  readCachedPreferencesBootstrap,
  savePreferences,
  setAccountOnboardingPlanSelection,
  setCurrentAccountId,
  setAccountPlan,
  setOnboardingPromoDecision,
  startBillingCheckout,
  submitPayFastPaymentForm,
  trackEvent,
} from "@/lib/api";
import { resolveGiftedDays } from "@/lib/onboarding-gift";
import { buildPaidConversionPayload } from "@/lib/billing-analytics";
import { getBillingStatusBanner, getPaidConversionStorageKey } from "@/lib/billing-flow";
import { redirectToAuthIfRequired, redirectToAuthIfSignedOut } from "@/lib/auth-required-redirect";
import { useI18n } from "@/lib/i18n";
import { routeOnboardingCheckoutEmailGateIfNeeded } from "@/lib/onboarding-checkout-email-gate";
import {
  ONBOARDING_DESIGN_MODE,
  currentRequestAllowsLocalOnboardingPreview,
  getOnboardingDesignState,
  getOnboardingEffectivePlan,
  readOnboardingDesignParams,
  updateOnboardingDesignState,
} from "@/lib/onboarding-design-mode";
import {
  canSavePreferencesForPlan,
  getPlanSeededPreferences,
  getRequiredPlanForPreferences,
  hasCustomizedPreferences,
  preferencesFromApi,
  preferencesToApi,
} from "@/lib/preferences";
import { consumePreferencesEntryTelemetry } from "@/lib/preferences-entry-telemetry";
import { deriveLinkingPhoneDisplayValue } from "@/lib/linking-phone";
import { buildSelfChatPlanSummary, buildSelfChatPreviewMessages } from "@/lib/onboarding-self-chat";
import { getPlanLabel, maxPlan, planIndex } from "@/lib/plans";
import { getStoredShareLinkReferrerName } from "@/lib/share-link-entry";
import { formatSettingsPlanSummary } from "@/lib/settings-shell";
import {
  buildSelfChatRelinkUrl,
  buildProfileDisplayName,
  resolveSelfChatName,
} from "@/lib/self-chat";
import { getStableLiveSetupCacheSnapshot } from "@/lib/live-setup-cache";

const DESIGN_STEPS = ["pricing", "preferences", "message"];
const LIVE_STEPS = ["preferences", "pricing", "message"];
const PREFERENCE_DRAFT_STORAGE_PREFIX = "mincool.setupPreferenceDraft";
const ONBOARDING_INCLUDED_PLAN = "fun";

function formatLinkedNumberLabel(phoneNumber) {
  const formattedNumber = deriveLinkingPhoneDisplayValue(phoneNumber, "");
  return formattedNumber || String(phoneNumber || "").trim() || "Linked number";
}

function hasDistinctLinkedNumberName(account) {
  const displayName = String(account?.displayName || "").trim();
  if (!displayName) {
    return false;
  }

  const normalizedNameDigits = displayName.replace(/\D+/g, "");
  const normalizedPhoneDigits = String(account?.phoneNumber || "").replace(/\D+/g, "");
  if (normalizedNameDigits && normalizedNameDigits === normalizedPhoneDigits) {
    return false;
  }

  const normalizeText = (value) => String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
  const normalizedDisplayName = normalizeText(displayName);
  const normalizedPhoneLabel = normalizeText(formatLinkedNumberLabel(account?.phoneNumber));
  const normalizedRawPhone = normalizeText(account?.phoneNumber);

  return normalizedDisplayName !== normalizedPhoneLabel && normalizedDisplayName !== normalizedRawPhone;
}

function canUseWindow() {
  return typeof window !== "undefined";
}

function resolveLoadedPreferencesPayload(nextPreferences, fallbackPreferences) {
  if (
    nextPreferences
    && typeof nextPreferences === "object"
    && Object.keys(nextPreferences).length > 0
  ) {
    return nextPreferences;
  }
  return fallbackPreferences;
}

function preferenceDraftStorageKey(accountId) {
  return `${PREFERENCE_DRAFT_STORAGE_PREFIX}.${String(accountId || "unknown").trim() || "unknown"}`;
}

function readPreferenceDraft(accountId) {
  if (!canUseWindow() || !accountId) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(preferenceDraftStorageKey(accountId));
    return raw ? preferencesFromApi(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writePreferenceDraft(accountId, preferences) {
  if (!canUseWindow() || !accountId) {
    return;
  }
  window.sessionStorage.setItem(
    preferenceDraftStorageKey(accountId),
    JSON.stringify(preferencesToApi(preferences)),
  );
}

function clearPreferenceDraft(accountId) {
  if (!canUseWindow() || !accountId) {
    return;
  }
  window.sessionStorage.removeItem(preferenceDraftStorageKey(accountId));
}

function resolveSelfChatPhoneNumber(payload) {
  return String(payload?.account?.phoneNumber || "").trim();
}

function buildPlanPriceIdMap(plans = []) {
  return plans.reduce((accumulator, plan) => {
    const planCode = String(plan?.code || "").trim();
    if (!planCode) {
      return accumulator;
    }
    accumulator[planCode] = (plan?.prices || []).reduce((priceAccumulator, price) => {
      const billingCycle = String(price?.billingCycle || "monthly").trim().toLowerCase() || "monthly";
      const priceId = String(price?.id || "").trim();
      if (priceId) {
        priceAccumulator[billingCycle] = priceId;
      }
      return priceAccumulator;
    }, {});
    return accumulator;
  }, {});
}

function normalizeOnboardingPlanSelection(plan) {
  const normalized = String(plan || "").trim().toLowerCase();
  return ["free", "fun", "pro"].includes(normalized) ? normalized : "";
}

function isOnboardingCheckoutTransaction(transaction) {
  const metadata = transaction?.metadata && typeof transaction.metadata === "object"
    ? transaction.metadata
    : {};
  return String(metadata.return_context || "").trim().toLowerCase() === "onboarding"
    || String(metadata.source || "").trim() === "setup_pricing";
}

function normalizeSelectedCadence(value, fallback = "") {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "monthly" || normalized === "annual") {
    return normalized;
  }
  return fallback;
}

export default function Setup() {
  const { tx } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const liveSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const localPreviewRequested = currentRequestAllowsLocalOnboardingPreview(location.search);
  const setupPreviewEnabled = ONBOARDING_DESIGN_MODE || localPreviewRequested;
  const forceLiveSetup = setupPreviewEnabled && liveSearchParams.get("forceLive") === "1";
  const useDesignSetupFlow = setupPreviewEnabled && !forceLiveSetup;
  const queryAccountId = liveSearchParams.get("accountId") || "";
  const liveStep = LIVE_STEPS.includes(liveSearchParams.get("step")) ? liveSearchParams.get("step") : "preferences";
  const liveMode = liveSearchParams.get("mode") === "new" ? "new" : "existing";
  const selectedPlan = normalizeOnboardingPlanSelection(liveSearchParams.get("selectedPlan"));
  const selectedCadence = normalizeSelectedCadence(liveSearchParams.get("selectedCadence"));
  const fromPreferences = liveSearchParams.get("fromPreferences") === "1";
  const payment = liveSearchParams.get("payment") || "";
  const transactionId = liveSearchParams.get("transactionId") || "";
  const storedCurrentAccountId = getCurrentAccountId() || "";
  const protectedExistingPreferencesRoute = !useDesignSetupFlow && liveMode === "existing" && liveStep === "preferences";
  const liveNeedsFreshServerState = !useDesignSetupFlow
    && liveMode === "new"
    && (
      liveStep === "message"
      || payment === "pending"
      || payment === "complete"
      || payment === "cancelled"
      || payment === "failed"
      || payment === "incomplete"
      || Boolean(transactionId)
    );
  const liveSetupCacheStateRef = useRef(null);
  const liveSetupCacheState = getStableLiveSetupCacheSnapshot(liveSetupCacheStateRef.current, {
    useDesignSetupFlow,
    liveNeedsFreshServerState,
    queryAccountId,
    storedAccountId: storedCurrentAccountId,
    liveMode,
    liveStep,
    readCachedPreferencesBootstrap,
    readCachedMyAccount,
    readCachedBillingSubscription,
    readCachedMyAccounts,
  });
  liveSetupCacheStateRef.current = liveSetupCacheState;
  const { account: cachedInitialAccount, billing: cachedInitialBilling, accountsPayload: cachedAccountsPayload } = protectedExistingPreferencesRoute
    ? { account: null, billing: null, accountsPayload: null }
    : liveSetupCacheState.snapshot;
  const [liveAccount, setLiveAccount] = useState(cachedInitialAccount);
  const [liveBilling, setLiveBilling] = useState(cachedInitialBilling);
  const [liveOnboardingBillingResolved, setLiveOnboardingBillingResolved] = useState(
    useDesignSetupFlow || !liveNeedsFreshServerState,
  );
  const [livePreferenceDraft, setLivePreferenceDraft] = useState(null);
  const [livePromoOffer, setLivePromoOffer] = useState(null);
  const [liveSettingsBilling, setLiveSettingsBilling] = useState(cachedInitialBilling);
  const [liveSettingsBillingLoading, setLiveSettingsBillingLoading] = useState(false);
  const [liveLinkedNumbers, setLiveLinkedNumbers] = useState(
    Array.isArray(cachedAccountsPayload?.accounts) ? cachedAccountsPayload.accounts : [],
  );
  const [liveLinkedNumbersLoading, setLiveLinkedNumbersLoading] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [liveBillingError, setLiveBillingError] = useState("");
  const [liveDraftError, setLiveDraftError] = useState("");
  const [liveLoading, setLiveLoading] = useState(!useDesignSetupFlow && !cachedInitialAccount);
  const [applyingDraft, setApplyingDraft] = useState(false);
  const [livePreferenceDraftHydrated, setLivePreferenceDraftHydrated] = useState(useDesignSetupFlow);
  const [liveCompletionClaimed, setLiveCompletionClaimed] = useState(false);
  const [liveCompletionRetryCount, setLiveCompletionRetryCount] = useState(0);
  const [liveGiftedDays, setLiveGiftedDays] = useState(liveMode === "new" ? 31 : 0);
  const [livePendingOnboardingPlanSelection, setLivePendingOnboardingPlanSelection] = useState("");
  const [liveMessageAccountLoaded, setLiveMessageAccountLoaded] = useState(false);
  const [liveMessageBillingLoaded, setLiveMessageBillingLoaded] = useState(false);
  const incompleteCheckoutRedirectedRef = useRef(false);
  const liveStoredReferrerName = getStoredShareLinkReferrerName();
  const liveOnboardingCompletedAt = liveAccount?.account?.onboardingCompletedAt || null;
  const liveHasCompletedOnboarding = Boolean(liveOnboardingCompletedAt) || liveMode === "existing" || liveCompletionClaimed;
  const liveIsOnboarding = !liveHasCompletedOnboarding;
  const liveStoredPreferences = useMemo(
    () => preferencesFromApi(liveAccount?.preferences || {}),
    [liveAccount],
  );
  const livePlan = liveAccount?.account?.plan || "free";
  const liveIncludedPlan = liveMode === "new" && liveIsOnboarding ? ONBOARDING_INCLUDED_PLAN : "free";
  const liveEffectivePlan = maxPlan(livePlan, liveIncludedPlan);
  const livePreferences = useMemo(() => {
    if (livePreferenceDraft) {
      return livePreferenceDraft;
    }

    if (!liveIsOnboarding || liveMode !== "new" || hasCustomizedPreferences(liveStoredPreferences)) {
      return liveStoredPreferences;
    }

    return getPlanSeededPreferences(liveEffectivePlan, liveStoredPreferences);
  }, [liveEffectivePlan, liveIsOnboarding, liveMode, livePreferenceDraft, liveStoredPreferences]);
  const liveAccountId = protectedExistingPreferencesRoute ? liveAccount?.account?.id || "" : queryAccountId || liveAccount?.account?.id || storedCurrentAccountId;
  const liveRouteSuffix = `${forceLiveSetup ? "&forceLive=1" : ""}${liveAccountId ? `&accountId=${encodeURIComponent(liveAccountId)}` : ""}`;
  const liveSelfChatRelinkUrl = liveAccountId ? buildSelfChatRelinkUrl({ origin: canUseWindow() ? window.location.origin : "", accountId: liveAccountId }) : "";
  const liveStatusBanner = liveBillingError
    ? {
      title: "Something went wrong",
      copy: liveBillingError,
      tone: "danger",
    }
    : getBillingStatusBanner(payment);
  const liveLatestTransaction = liveBilling?.latestTransaction || null;
  const liveLatestOnboardingTransaction = isOnboardingCheckoutTransaction(liveLatestTransaction)
    ? liveLatestTransaction
    : null;
  const liveStoredOnboardingPlanSelection = normalizeOnboardingPlanSelection(
    liveAccount?.account?.onboardingPlanSelection,
  );
  const liveOnboardingPlanSelection = normalizeOnboardingPlanSelection(
    livePendingOnboardingPlanSelection || liveStoredOnboardingPlanSelection,
  );
  const liveDesiredOnboardingPlan = normalizeOnboardingPlanSelection(
    selectedPlan || liveOnboardingPlanSelection || liveEffectivePlan,
  ) || liveEffectivePlan;
  const liveHasResolvedPaidOnboarding = Boolean(
    liveBilling?.subscription?.status === "active"
      || liveLatestOnboardingTransaction?.status === "complete",
  );
  const liveRequiresPaidUpgrade = planIndex(liveDesiredOnboardingPlan) > planIndex(liveEffectivePlan);
  const liveCanEnterMessageStep = (
    liveMode !== "new"
    || !liveIsOnboarding
    || !liveRequiresPaidUpgrade
    || liveHasResolvedPaidOnboarding
  );
  const liveStandardPriceIds = useMemo(
    () => buildPlanPriceIdMap(liveBilling?.plans || []),
    [liveBilling?.plans],
  );
  const livePromoPriceIds = useMemo(
    () => buildPlanPriceIdMap(livePromoOffer?.plans || []),
    [livePromoOffer?.plans],
  );
  const livePromoOfferActive = liveMode === "new" && Boolean(livePromoOffer?.eligible);
  const livePricingOfferMode = livePromoOfferActive ? "promo" : "live";
  const livePricingPriceIds = livePromoOfferActive ? livePromoPriceIds : liveStandardPriceIds;
  const livePricingInitialPlan = selectedPlan || liveOnboardingPlanSelection || liveEffectivePlan;
  const liveGiftedPlan = liveGiftedDays > 0 ? ONBOARDING_INCLUDED_PLAN : "free";
  const liveResolvedMessagePlan = maxPlan(
    normalizeOnboardingPlanSelection(liveBilling?.subscription?.planCode || livePlan) || livePlan,
    maxPlan(liveEffectivePlan, liveGiftedPlan),
  );
  const liveLatestTransactionCadence = normalizeSelectedCadence(liveLatestTransaction?.metadata?.billing_cycle);
  const livePricingInitialCadence = livePromoOfferActive
    ? (selectedCadence || liveLatestTransactionCadence || "annual")
    : "monthly";
  const liveSelectedLinkedAccount = useMemo(
    () => liveLinkedNumbers.find((account) => account.id === liveAccountId) || null,
    [liveAccountId, liveLinkedNumbers],
  );
  const liveShowHeaderNumberSelector = !liveLinkedNumbersLoading && liveLinkedNumbers.length > 1;
  useEffect(() => {
    if (!useDesignSetupFlow && liveMode === "new" && liveStep === "message") {
      setLiveMessageAccountLoaded(false);
      setLiveMessageBillingLoaded(false);
      return;
    }
    setLiveMessageAccountLoaded(true);
    setLiveMessageBillingLoaded(true);
  }, [liveMode, liveStep, useDesignSetupFlow, liveAccountId, payment, transactionId]);

  useEffect(() => {
    if (useDesignSetupFlow) {
      return undefined;
    }
    let cancelled = false;

    async function loadAccount() {
      try {
        setLiveLoading(!cachedInitialAccount);
        setLiveError("");
        if (protectedExistingPreferencesRoute && await redirectToAuthIfSignedOut({ navigate, location })) return;
        const payload = protectedExistingPreferencesRoute
          ? await getPreferencesBootstrap(queryAccountId, { forceFresh: true })
          : await getMyAccount(queryAccountId, { forceFresh: !cachedInitialAccount });
        if (!cancelled) {
          setLiveAccount((current) => (
            payload
              ? {
                ...payload,
                preferences: resolveLoadedPreferencesPayload(payload.preferences, current?.preferences),
              }
              : payload
          ));
        }
      } catch (error) {
        if (!cancelled) {
          if (
            protectedExistingPreferencesRoute
            && redirectToAuthIfRequired(error, { navigate, location })
          ) {
            return;
          }
          setLiveError(error instanceof Error ? error.message : "Could not load your account.");
        }
      } finally {
        if (!cancelled) {
          setLiveLoading(false);
          if (liveMode === "new" && liveStep === "message") {
            setLiveMessageAccountLoaded(true);
          }
        }
      }
    }

    void loadAccount();

    return () => {
      cancelled = true;
    };
  }, [cachedInitialAccount, protectedExistingPreferencesRoute, liveStep, location, navigate, payment, queryAccountId, transactionId, useDesignSetupFlow]);

  useEffect(() => {
    if (useDesignSetupFlow || !liveAccountId || liveMode !== "new") {
      setLiveBilling(null);
      setLiveOnboardingBillingResolved(useDesignSetupFlow || liveMode !== "new");
      return undefined;
    }
    let cancelled = false;

    async function loadBilling() {
      try {
        setLiveOnboardingBillingResolved(Boolean(cachedInitialBilling));
        setLiveBillingError("");
        const payload = await getBillingSubscription(liveAccountId, { forceFresh: !cachedInitialBilling });
        if (!cancelled) {
          setLiveBilling(payload);
        }
      } catch (error) {
        if (!cancelled) {
          setLiveBilling(null);
          setLiveBillingError(error instanceof Error ? error.message : "Could not load billing.");
        }
      } finally {
        if (!cancelled) {
          setLiveOnboardingBillingResolved(true);
          if (liveMode === "new" && liveStep === "message") {
            setLiveMessageBillingLoaded(true);
          }
        }
      }
    }

    void loadBilling();

    return () => {
      cancelled = true;
    };
  }, [cachedInitialBilling, liveAccountId, liveMode, liveStep, payment, transactionId, useDesignSetupFlow]);

  useEffect(() => {
    setLiveCompletionClaimed(Boolean(liveOnboardingCompletedAt) || liveMode === "existing");
  }, [liveAccount?.account?.id, liveMode, liveOnboardingCompletedAt]);

  useEffect(() => {
    setLiveCompletionRetryCount(0);
  }, [liveAccount?.account?.id, liveMode, liveStep]);

  useEffect(() => {
    if (!livePendingOnboardingPlanSelection) {
      return;
    }
    if (livePendingOnboardingPlanSelection === liveStoredOnboardingPlanSelection) {
      setLivePendingOnboardingPlanSelection("");
    }
  }, [livePendingOnboardingPlanSelection, liveStoredOnboardingPlanSelection]);

  useEffect(() => {
    setLivePendingOnboardingPlanSelection("");
  }, [liveAccount?.account?.id]);

  useEffect(() => {
    setLiveGiftedDays(liveMode === "new" ? resolveGiftedDays({ giftedDays: liveAccount?.giftedDays, giftedMonths: liveAccount?.giftedMonths ?? 1 }) : 0);
  }, [liveAccount?.account?.id, liveAccount?.giftedDays, liveAccount?.giftedMonths, liveMode]);

  useEffect(() => {
    if (useDesignSetupFlow || liveMode !== "existing" || liveStep !== "preferences" || !liveAccountId) {
      setLiveSettingsBilling(null);
      setLiveSettingsBillingLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadSettingsBilling() {
      try {
        setLiveSettingsBillingLoading(!cachedInitialBilling && !liveSettingsBilling);
        const payload = await getBillingSubscription(liveAccountId);
        if (!cancelled) {
          setLiveSettingsBilling(payload);
        }
      } catch {
        if (!cancelled) {
          setLiveSettingsBilling(null);
        }
      } finally {
        if (!cancelled) {
          setLiveSettingsBillingLoading(false);
        }
      }
    }

    void loadSettingsBilling();

    return () => {
      cancelled = true;
    };
  }, [cachedInitialBilling, liveAccountId, liveMode, liveStep, useDesignSetupFlow]);

  useEffect(() => {
    if (useDesignSetupFlow || liveMode !== "existing" || liveStep !== "preferences") {
      setLiveLinkedNumbers([]);
      setLiveLinkedNumbersLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadLinkedNumbers() {
      try {
        setLiveLinkedNumbersLoading(true);
        const payload = await listMyAccounts({ forceFresh: true });
        if (!cancelled) {
          setLiveLinkedNumbers(Array.isArray(payload?.accounts) ? payload.accounts : []);
        }
      } catch (error) {
        if (!cancelled) {
          if (redirectToAuthIfRequired(error, { navigate, location })) {
            return;
          }
          setLiveLinkedNumbers([]);
        }
      } finally {
        if (!cancelled) {
          setLiveLinkedNumbersLoading(false);
        }
      }
    }

    void loadLinkedNumbers();

    return () => {
      cancelled = true;
    };
  }, [liveMode, liveStep, location, navigate, useDesignSetupFlow]);

  useEffect(() => {
    if (useDesignSetupFlow || !liveAccountId) {
      setLivePreferenceDraft(null);
      setLivePreferenceDraftHydrated(true);
      return;
    }
    setLivePreferenceDraftHydrated(false);
    setLivePreferenceDraft(readPreferenceDraft(liveAccountId));
    setLivePreferenceDraftHydrated(true);
  }, [liveAccountId, useDesignSetupFlow]);

  useEffect(() => {
    if (useDesignSetupFlow || !liveAccountId || liveMode !== "new" || liveStep !== "pricing") {
      setLivePromoOffer(null);
      return undefined;
    }
    let cancelled = false;

    async function loadPromoOffer() {
      try {
        const payload = await getOnboardingPromoOffer(liveAccountId);
        if (!cancelled) {
          setLivePromoOffer(payload);
        }
      } catch {
        if (!cancelled) {
          setLivePromoOffer(null);
        }
      }
    }

    void loadPromoOffer();

    return () => {
      cancelled = true;
    };
  }, [liveAccountId, liveMode, liveStep, useDesignSetupFlow]);

  useEffect(() => {
    incompleteCheckoutRedirectedRef.current = false;
  }, [liveAccountId, liveStep, payment, transactionId]);

  useEffect(() => {
    if (
      useDesignSetupFlow
      || liveLoading
      || liveMode !== "new"
      || liveStep !== "message"
      || !liveAccountId
      || !liveIsOnboarding
      || !liveOnboardingBillingResolved
      || !liveMessageAccountLoaded
      || !liveMessageBillingLoaded
      || liveCanEnterMessageStep
      || incompleteCheckoutRedirectedRef.current
    ) {
      return;
    }

    incompleteCheckoutRedirectedRef.current = true;
    const incompleteTransaction = liveLatestOnboardingTransaction
      && liveLatestOnboardingTransaction.status !== "complete"
      ? liveLatestOnboardingTransaction
      : null;
    if (incompleteTransaction) {
      trackEvent("onboarding_checkout_incomplete", {
        selected_plan: normalizeOnboardingPlanSelection(incompleteTransaction.metadata?.plan_code) || liveDesiredOnboardingPlan,
        source: String(incompleteTransaction.metadata?.source || "setup_pricing").trim() || "setup_pricing",
        offer_type: String(incompleteTransaction.metadata?.offer_type || "standard").trim() || "standard",
      });
    }
    const redirectPlan = normalizeOnboardingPlanSelection(
      incompleteTransaction?.metadata?.plan_code || liveOnboardingPlanSelection,
    );
    const redirectPayment = incompleteTransaction ? "incomplete" : "";
    const redirectTransactionId = incompleteTransaction?.id || "";
    navigate(
      `/setup?step=pricing&mode=new${liveRouteSuffix}${redirectPlan ? `&selectedPlan=${encodeURIComponent(redirectPlan)}` : ""}${redirectPayment ? `&payment=${encodeURIComponent(redirectPayment)}` : ""}${redirectTransactionId ? `&transactionId=${encodeURIComponent(redirectTransactionId)}` : ""}`,
      { replace: true },
    );
  }, [
    incompleteCheckoutRedirectedRef,
    liveAccountId,
    liveCanEnterMessageStep,
    liveIsOnboarding,
    liveMessageAccountLoaded,
    liveMessageBillingLoaded,
    liveLatestOnboardingTransaction,
    liveLoading,
    liveMode,
    liveOnboardingBillingResolved,
    liveDesiredOnboardingPlan,
    liveOnboardingPlanSelection,
    liveRouteSuffix,
    liveStep,
    navigate,
    payment,
    transactionId,
    useDesignSetupFlow,
  ]);

  useEffect(() => {
    if (useDesignSetupFlow || !liveBilling || payment !== "pending" || !transactionId || !liveAccountId) {
      return undefined;
    }
    const latestTransaction = liveBilling.latestTransaction;
    if (latestTransaction?.id === transactionId) {
      if (latestTransaction.status === "complete") {
        navigate(
          `/setup?step=message&mode=${liveMode}${liveRouteSuffix}&payment=complete&transactionId=${encodeURIComponent(transactionId)}`,
          { replace: true },
        );
        return undefined;
      }
      if (latestTransaction.status === "failed" || latestTransaction.status === "cancelled") {
        const retryCadence = normalizeSelectedCadence(
          latestTransaction.metadata?.billing_cycle,
          selectedCadence,
        );
        navigate(
          `/setup?step=pricing&mode=${liveMode}${liveRouteSuffix}&selectedPlan=${encodeURIComponent(selectedPlan)}${retryCadence ? `&selectedCadence=${encodeURIComponent(retryCadence)}` : ""}&payment=${encodeURIComponent(latestTransaction.status)}&transactionId=${encodeURIComponent(transactionId)}`,
          { replace: true },
        );
        return undefined;
      }
    }
    if (!canUseWindow()) {
      return undefined;
    }
    const timeoutId = window.setTimeout(async () => {
      try {
        const payload = await getBillingSubscription(liveAccountId, { forceFresh: true });
        setLiveBilling(payload);
      } catch {
        // Keep polling failures quiet while the callback catches up.
      }
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [liveAccountId, liveBilling, liveMode, navigate, payment, selectedCadence, selectedPlan, transactionId, liveRouteSuffix, useDesignSetupFlow]);

  useEffect(() => {
    if (
      useDesignSetupFlow
      || liveLoading
      || liveMode !== "existing"
      || liveStep !== "preferences"
    ) {
      return;
    }
    const payload = consumePreferencesEntryTelemetry();
    if (!payload) {
      return;
    }
    const startedAt = Number(payload.startedAt || 0);
    const readyAt = typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
    trackEvent("preferences_screen_ready", {
      entry_path: String(payload.entryPath || "unknown"),
      destination: String(payload.destination || "preferences"),
      total_duration_ms: startedAt > 0 ? Math.round(Math.max(0, readyAt - startedAt)) : null,
      gate_lookup_duration_ms: payload.gateLookupDurationMs ?? null,
      account_fetch_duration_ms: payload.accountFetchDurationMs ?? null,
      billing_fetch_duration_ms: payload.billingFetchDurationMs ?? null,
      server_app_duration_ms: payload.serverAppDurationMs ?? null,
      server_auth_duration_ms: payload.serverAuthDurationMs ?? null,
      server_db_duration_ms: payload.serverDbDurationMs ?? null,
      used_cached_account: Boolean(cachedInitialAccount),
      used_cached_billing: Boolean(cachedInitialBilling),
      used_single_loader: Boolean(payload.usedSingleLoader),
    });
  }, [
    cachedInitialAccount,
    cachedInitialBilling,
    liveLoading,
    liveMode,
    liveStep,
    useDesignSetupFlow,
  ]);

  useEffect(() => {
    if (useDesignSetupFlow || payment !== "complete" || !transactionId || !canUseWindow()) {
      return;
    }
    const key = getPaidConversionStorageKey(transactionId);
    if (window.sessionStorage.getItem(key)) {
      return;
    }
    const metadata = liveBilling?.latestTransaction?.metadata || {};
    trackEvent("paid_conversion", buildPaidConversionPayload({
      fromPlan: metadata.previous_plan || "free",
      toPlan: metadata.plan_code || livePlan || "free",
      metadata,
    }));
    window.sessionStorage.setItem(key, "1");
  }, [liveBilling, livePlan, payment, transactionId, useDesignSetupFlow]);

  useEffect(() => {
    if (
      useDesignSetupFlow
      || payment !== "complete"
      || !liveAccountId
      || !livePreferenceDraft
      || liveLoading
      || applyingDraft
      || !canSavePreferencesForPlan(livePreferenceDraft, livePlan)
    ) {
      return undefined;
    }

    let cancelled = false;

    async function applyPreferenceDraft() {
      try {
        setApplyingDraft(true);
        setLiveDraftError("");
        const payload = await savePreferences(preferencesToApi(livePreferenceDraft), { accountId: liveAccountId });
        if (cancelled) {
          return;
        }
        clearPreferenceDraft(liveAccountId);
        setLivePreferenceDraft(null);
        setLiveAccount((current) => (
          current
            ? { ...current, preferences: payload.preferences }
            : current
        ));
      } catch (error) {
        if (!cancelled) {
          setLiveDraftError(error instanceof Error ? error.message : "Could not apply your saved preferences.");
        }
      } finally {
        if (!cancelled) {
          setApplyingDraft(false);
        }
      }
    }

    void applyPreferenceDraft();

    return () => {
      cancelled = true;
    };
  }, [
    applyingDraft,
    liveAccountId,
    liveLoading,
    livePlan,
    livePreferenceDraft,
    payment,
    useDesignSetupFlow,
  ]);

  useEffect(() => {
    if (!useDesignSetupFlow) {
      return undefined;
    }
    const params = readOnboardingDesignParams(location.search);
    if (params.demoStep !== "message") {
      return undefined;
    }
    const state = getOnboardingDesignState();
    if (state.onboardingCompletedAt) {
      return undefined;
    }
    updateOnboardingDesignState((current) => ({
      ...current,
      onboardingCompletedAt: new Date().toISOString(),
    }));
    return undefined;
  }, [location.search, useDesignSetupFlow]);

  useEffect(() => {
    if (
      useDesignSetupFlow
      || liveLoading
      || liveStep !== "message"
      || !liveAccountId
      || !liveIsOnboarding
      || !livePreferenceDraftHydrated
      || applyingDraft
      || Boolean(livePreferenceDraft)
      || !liveCanEnterMessageStep
    ) {
      return undefined;
    }

    let cancelled = false;
    let retryTimeoutId = null;

    async function markOnboardingComplete() {
      try {
        const payload = await completeAccountOnboarding(liveAccountId);
        if (!cancelled && payload) {
          setLiveCompletionClaimed(true);
          setLiveCompletionRetryCount(0);
          setLiveGiftedDays(resolveGiftedDays(payload));
          setLiveAccount((current) => (
            current
              ? {
                ...current,
                account: payload.account
                  ? {
                    ...current.account,
                    ...payload.account,
                  }
                  : {
                    ...current.account,
                    onboardingCompletedAt: payload.onboardingCompletedAt,
                    plan: payload.currentPlan || current.account?.plan,
                  },
                preferences: resolveLoadedPreferencesPayload(current.preferences, payload.preferences),
              }
              : current
          ));
        }
      } catch (error) {
        console.warn("Failed to mark onboarding complete", error);
        if (!cancelled && canUseWindow()) {
          const retryDelayMs = Math.min(15000, 3000 * (liveCompletionRetryCount + 1));
          retryTimeoutId = window.setTimeout(() => {
            setLiveCompletionRetryCount((count) => count + 1);
          }, retryDelayMs);
        }
      }
    }

    void markOnboardingComplete();

    return () => {
      cancelled = true;
      if (retryTimeoutId !== null && canUseWindow()) {
        window.clearTimeout(retryTimeoutId);
      }
    };
  }, [
    liveAccountId,
    livePreferenceDraft,
    livePreferenceDraftHydrated,
    liveCanEnterMessageStep,
    liveCompletionRetryCount,
    liveIsOnboarding,
    liveLoading,
    applyingDraft,
    liveStep,
    useDesignSetupFlow,
  ]);

  if (useDesignSetupFlow) {
    const state = getOnboardingDesignState();
    const params = readOnboardingDesignParams(location.search);
    const searchParams = new URLSearchParams(location.search);
    const stepFromQuery = DESIGN_STEPS.includes(params.demoStep) ? params.demoStep : "preferences";
    const returnPreference = searchParams.get("offerReturn");
    const pricingReturnStep = returnPreference === "preferences"
      ? "preferences"
      : returnPreference === "message"
        ? "message"
        : "message";
    const effectivePlan = getOnboardingEffectivePlan(state, params.demoPlan);
    const designHasCompletedOnboarding = Boolean(state.onboardingCompletedAt) || state.auth.userMode === "existing";
    const designIsOnboarding = !designHasCompletedOnboarding;
    const designChatName = buildProfileDisplayName(state.auth) || "Your WhatsApp chat";
    const designChatPhoneNumber = "27820000001";
    const designGiftedMonths = state.auth.userMode === "new" ? 1 : 0;

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={stepFromQuery}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {stepFromQuery === "pricing" && (
            <PricingOffer
              offerMode="promo"
              currentPlan={effectivePlan}
              initialPlan={state.billing.selectedPlan}
              initialCadence={state.billing.cadence}
              includedPlan={designIsOnboarding ? effectivePlan : ""}
              giftedMonths={designGiftedMonths}
              onBack={() => navigate("/setup?demoStep=preferences")}
              onSkip={async () => {
                updateOnboardingDesignState((current) => ({
                  ...current,
                  billing: {
                    ...current.billing,
                    selectedPlan: effectivePlan,
                    purchasedPlan: null,
                  },
                }));
                navigate(`/setup?demoStep=${pricingReturnStep}`);
              }}
              onContinue={async ({ plan, cadence }) => {
                await setAccountPlan(plan, { cadence });
                updateOnboardingDesignState((current) => ({
                  ...current,
                  billing: {
                    ...current.billing,
                    cadence,
                    selectedPlan: plan,
                    purchasedPlan: plan,
                  },
                }));
                navigate(`/setup?demoStep=${pricingReturnStep}`);
              }}
            />
          )}

          {stepFromQuery === "preferences" && (
            <PreferencesSetup
              accountId={getCurrentAccountId()}
              effectivePlan={effectivePlan}
              initialPreferences={state.preferences}
              savedPreferences={state.preferences}
              isOnboarding={designIsOnboarding}
              giftedMonths={designGiftedMonths}
              onSave={async (preferences) => {
                updateOnboardingDesignState((current) => ({
                  ...current,
                  preferences,
                }));
                if (!designIsOnboarding) {
                  return;
                }
                const requiredPlan = getRequiredPlanForPreferences(preferences);
                trackEvent("config_completed", {
                  plan_at_completion: effectivePlan,
                });
                navigate(
                  state.auth.userMode === "existing"
                    ? "/setup?demoStep=message"
                    : planIndex(requiredPlan) > planIndex(effectivePlan)
                      ? "/setup?demoStep=pricing&offerReturn=message"
                      : "/setup?demoStep=message",
                );
              }}
              onUpgrade={(requiredPlan, preferences) => {
                updateOnboardingDesignState((current) => ({
                  ...current,
                  preferences,
                  billing: {
                    ...current.billing,
                    selectedPlan: requiredPlan,
                  },
                }));
                navigate("/setup?demoStep=pricing&offerReturn=message");
              }}
            />
          )}

          {stepFromQuery === "message" && (
            <SelfChatTest
              title="Open your self chat"
              description="This preview shows what Min will do there. Then send yourself a voice note to test it."
              chatName={designChatName}
              chatSubtitle="Your self chat in WhatsApp"
              messages={buildSelfChatPreviewMessages({
                giftedMonths: designGiftedMonths,
                preferences: state.preferences,
              })}
              phoneNumber={designChatPhoneNumber}
              planSummary={buildSelfChatPlanSummary(effectivePlan, {
                giftedMonths: designGiftedMonths,
                preferences: state.preferences,
              })}
              ctaLabel="Open your self chat"
              secondaryCtaLabel="View my preferences"
              onSecondaryAction={() => navigate("/setup?demoStep=preferences")}
            />
          )}
        </motion.div>
      </AnimatePresence>
    );
  }

  if (
    liveLoading
    || (
      liveMode === "new"
      && (liveStep === "pricing" || liveStep === "message")
      && !liveOnboardingBillingResolved
    )
    || (
      liveMode === "new"
      && liveStep === "message"
      && (!liveMessageAccountLoaded || !liveMessageBillingLoaded)
    )
  ) {
    return (
      <RouteLoadingShell
        title={tx("Loading your account…")}
      />
    );
  }

  if (liveError) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center px-5 text-center text-sm text-destructive">
        {tx(liveError)}
      </div>
    );
  }

  const liveSelfChatName = resolveSelfChatName(liveAccount);
  const liveSelfChatPhoneNumber = resolveSelfChatPhoneNumber(liveAccount);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={liveStep}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {liveDraftError ? (
          <div className="mx-auto mt-4 w-full max-w-3xl rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-4 text-sm text-foreground">
            {tx(liveDraftError)}
          </div>
        ) : null}

        {liveStep === "preferences" && (
          liveMode === "existing" ? (
            <SignedInSettingsShell
              currentSection="preferences"
              title="Preferences"
              onBack={() => navigate(-1)}
              planLabel={`${getPlanLabel(livePlan)} plan`}
              planTooltipCopy={formatSettingsPlanSummary(livePlan, liveSettingsBilling?.subscription)}
              planTooltipLoading={liveSettingsBillingLoading}
              sectionLinks={{
                referrals: liveAccountId ? `/referrals?accountId=${encodeURIComponent(liveAccountId)}` : "/referrals",
                account: liveAccountId ? `/account?accountId=${encodeURIComponent(liveAccountId)}` : "/account",
                billing: liveAccountId ? `/billing?accountId=${encodeURIComponent(liveAccountId)}` : "/billing",
              }}
              headerNavAccessory={liveShowHeaderNumberSelector ? (
                <div>
                  <Select
                    value={liveAccountId}
                    onValueChange={(accountId) => {
                      if (!accountId || accountId === liveAccountId) {
                        return;
                      }
                      setCurrentAccountId(accountId);
                      const nextSearchParams = new URLSearchParams(liveSearchParams);
                      nextSearchParams.set("accountId", accountId);
                      navigate(`${location.pathname}?${nextSearchParams.toString()}`, { replace: true });
                    }}
                  >
                    <SelectTrigger
                      aria-label={tx("WhatsApp number")}
                      className="h-9 w-[8.75rem] rounded-full border-border/70 bg-card text-left text-sm sm:h-10 sm:w-[11rem]"
                    >
                      <span className="truncate">
                        {formatLinkedNumberLabel(liveSelectedLinkedAccount?.phoneNumber)}
                      </span>
                    </SelectTrigger>
                    <SelectContent align="end">
                      {liveLinkedNumbers.map((account) => {
                        const phoneLabel = formatLinkedNumberLabel(account.phoneNumber);
                        const showDisplayName = hasDistinctLinkedNumberName(account);

                        return (
                          <SelectItem key={account.id} value={account.id} className="py-2 pl-3 pr-8">
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate font-medium text-foreground">{phoneLabel}</span>
                              {showDisplayName ? (
                                <span className="truncate text-xs text-muted-foreground">{account.displayName}</span>
                              ) : null}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            >
              <PreferencesSetup
                embedded
                accountId={liveAccountId}
                effectivePlan={liveEffectivePlan}
                initialPreferences={livePreferences}
                savedPreferences={liveStoredPreferences}
                isOnboarding={liveIsOnboarding}
                giftedDays={liveMode === "new" ? liveGiftedDays : 0}
                referrerName={liveStoredReferrerName}
                onSave={async (preferences) => {
                  const payload = await savePreferences(preferencesToApi(preferences), { accountId: liveAccountId });
                  clearPreferenceDraft(liveAccountId);
                  setLivePreferenceDraft(null);
                  setLiveDraftError("");
                  setLiveAccount((current) => (
                    current
                      ? { ...current, preferences: payload.preferences }
                      : current
                  ));
                  if (!liveIsOnboarding) {
                    return;
                  }
                  const requiredPlan = getRequiredPlanForPreferences(preferences);
                  trackEvent("config_completed", {
                    plan_at_completion: liveEffectivePlan,
                  });
                  navigate(
                    liveIsOnboarding && liveMode === "new" && planIndex(requiredPlan) > planIndex(liveEffectivePlan)
                      ? `/setup?step=pricing&mode=new${liveRouteSuffix}&selectedPlan=${encodeURIComponent(requiredPlan)}`
                      : `/setup?step=message&mode=${liveMode}${liveRouteSuffix}`,
                  );
                }}
                onUpgrade={(requiredPlan, preferences) => {
                  writePreferenceDraft(liveAccountId, preferences);
                  setLivePreferenceDraft(preferences);
                  setLiveDraftError("");
                  navigate(
                    `/setup?step=pricing&mode=${liveMode}${liveRouteSuffix}&selectedPlan=${requiredPlan}&fromPreferences=1`,
                  );
                }}
              />
            </SignedInSettingsShell>
          ) : (
            <PreferencesSetup
              accountId={liveAccountId}
              effectivePlan={liveEffectivePlan}
              initialPreferences={livePreferences}
              savedPreferences={liveStoredPreferences}
              isOnboarding={liveIsOnboarding}
              giftedDays={liveMode === "new" ? liveGiftedDays : 0}
              referrerName={liveStoredReferrerName}
              onSave={async (preferences) => {
                const payload = await savePreferences(preferencesToApi(preferences), { accountId: liveAccountId });
                clearPreferenceDraft(liveAccountId);
                setLivePreferenceDraft(null);
                setLiveDraftError("");
                setLiveAccount((current) => (
                  current
                    ? { ...current, preferences: payload.preferences }
                    : current
                ));
                if (!liveIsOnboarding) {
                  return;
                }
                const requiredPlan = getRequiredPlanForPreferences(preferences);
                trackEvent("config_completed", {
                  plan_at_completion: liveEffectivePlan,
                });
                navigate(
                  liveIsOnboarding && liveMode === "new" && planIndex(requiredPlan) > planIndex(liveEffectivePlan)
                    ? `/setup?step=pricing&mode=new${liveRouteSuffix}&selectedPlan=${encodeURIComponent(requiredPlan)}`
                    : `/setup?step=message&mode=${liveMode}${liveRouteSuffix}`,
                );
              }}
              onUpgrade={(requiredPlan, preferences) => {
                writePreferenceDraft(liveAccountId, preferences);
                setLivePreferenceDraft(preferences);
                setLiveDraftError("");
                navigate(
                  `/setup?step=pricing&mode=${liveMode}${liveRouteSuffix}&selectedPlan=${requiredPlan}&fromPreferences=1`,
                );
              }}
            />
          )
        )}

        {liveStep === "pricing" && (
          <PricingOffer
            offerMode={livePricingOfferMode}
            currentPlan={liveEffectivePlan}
            initialPlan={livePricingInitialPlan}
            initialCadence={livePricingInitialCadence}
            includedPlan={liveMode === "new" ? liveIncludedPlan : ""}
            giftedDays={liveMode === "new" ? liveGiftedDays : 0}
            referrerName={liveStoredReferrerName}
            checkoutCurrencyNote="Plans are shown in USD here. PayFast charges South African rand at checkout."
            banner={liveStatusBanner}
            priceIds={livePricingPriceIds}
            onBack={() => navigate(`/setup?step=preferences&mode=${liveMode}${liveRouteSuffix}`)}
            onSkip={async () => {
              try {
                setLiveBillingError("");
                const selectionPayload = await setAccountOnboardingPlanSelection(liveAccountId, liveEffectivePlan, {
                  source: "setup_pricing",
                  offerType: livePromoOfferActive ? "shortlink_onboarding" : "standard",
                });
                setLivePendingOnboardingPlanSelection(liveEffectivePlan);
                setLiveAccount((current) => (
                  current
                    ? {
                      ...current,
                      account: {
                        ...current.account,
                        onboardingPlanSelection: selectionPayload.onboardingPlanSelection,
                        onboardingPlanSelectionAt: selectionPayload.onboardingPlanSelectionAt,
                      },
                    }
                    : current
                ));
              } catch (error) {
                setLiveBillingError(error instanceof Error ? error.message : "Could not save your plan choice.");
                return;
              }
              if (fromPreferences) {
                clearPreferenceDraft(liveAccountId);
                setLivePreferenceDraft(null);
              }
              navigate(`/setup?step=message&mode=${liveMode}${liveRouteSuffix}&selectedPlan=${encodeURIComponent(liveEffectivePlan)}`);
              if (livePromoOfferActive) {
                void setOnboardingPromoDecision({
                  accountId: liveAccountId,
                  decision: "declined",
                })
                  .then(() => {
                    setLivePromoOffer((current) => (
                      current
                        ? {
                          ...current,
                          eligible: false,
                          reason: "declined",
                        }
                        : current
                    ));
                  })
                  .catch((error) => {
                    console.warn("Could not persist onboarding promo decline", error);
                  });
              }
            }}
            onContinue={async ({ plan, priceId, cadence }) => {
              const normalizedCadence = normalizeSelectedCadence(cadence, livePromoOfferActive ? livePricingInitialCadence : "monthly") || "monthly";
              try {
                setLiveBillingError("");
                const checkoutStartedAtMs =
                  typeof performance !== "undefined" && typeof performance.now === "function"
                    ? performance.now()
                    : Date.now();
                const selectionPayload = await setAccountOnboardingPlanSelection(liveAccountId, plan, {
                  source: "setup_pricing",
                  offerType: livePromoOfferActive ? "shortlink_onboarding" : "standard",
                });
                setLivePendingOnboardingPlanSelection(plan);
                setLiveAccount((current) => (
                  current
                    ? {
                      ...current,
                      account: {
                        ...current.account,
                        onboardingPlanSelection: selectionPayload.onboardingPlanSelection,
                        onboardingPlanSelectionAt: selectionPayload.onboardingPlanSelectionAt,
                      },
                    }
                    : current
                ));
                const checkout = await startBillingCheckout({
                  accountId: liveAccountId,
                  priceId,
                  toPlan: plan,
                  source: "setup_pricing",
                  returnContext: "onboarding",
                  onboardingMode: liveMode,
                  fromPlan: liveEffectivePlan,
                  billingCycle: normalizedCadence,
                  offerType: livePromoOfferActive ? "shortlink_onboarding" : "standard",
                  checkoutStartedAtMs,
                });
                submitPayFastPaymentForm(checkout);
              } catch (error) {
                if (await routeOnboardingCheckoutEmailGateIfNeeded(error, { accountId: liveAccountId, createHandoff: createOnboardingHandoff, forceLive: forceLiveSetup, mode: liveMode, navigate, onError: setLiveBillingError, selectedCadence: normalizedCadence, selectedPlan: plan })) {
                  return;
                }
                setLiveBillingError(error instanceof Error ? error.message : "Could not start checkout.");
              }
            }}
          />
        )}

        {liveStep === "message" && (
          <SelfChatTest
            title="Open your self chat"
            description="This preview shows what Min will do there. Then send yourself a voice note to test it."
            chatName={liveSelfChatName}
            chatSubtitle="Your self chat in WhatsApp"
            ctaLabel="Open your self chat"
            phoneNumber={liveSelfChatPhoneNumber}
            messages={buildSelfChatPreviewMessages({
              giftedDays: liveGiftedDays,
              preferences: livePreferences,
              relinkUrl: liveSelfChatRelinkUrl,
            })}
            planSummary={buildSelfChatPlanSummary(liveResolvedMessagePlan, {
              giftedDays: liveGiftedDays,
              preferences: livePreferences,
            })}
            secondaryCtaLabel="View my preferences"
            onSecondaryAction={() => navigate(`/setup?step=preferences&mode=${liveMode}${liveRouteSuffix}`)}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
